import { isEqual } from "lodash-es";
import {
  ItemType,
  TemplateSearchFilterPropertyFilterInput,
  TemplateSearchFiltersInput,
  TplApplicableFor,
} from "../../../../clients/graphqlTypes.js";

/**
 * The branch rule behind design D9's zero-result states. Kept as a pure
 * function so the rule (and its ordering) is unit-testable without mounting
 * the picker.
 *
 * The relaxed query (`getRelaxedCountFilters`) drops every quick/property
 * filter — quote-seeded or manually added — but keeps the current search
 * term, so `relaxedCount` always means exactly what clearing filters (and
 * only filters) would reveal:
 * - `relaxedCount > 0` → filters (whichever they are) are hiding matches;
 *   "Clear filters" truthfully reveals that count. Copy never presumes the
 *   cause is specifically the quote's own filters — it may be a filter
 *   added independently of the quote, and "Clear filters" clears both the
 *   same way.
 * - `relaxedCount === 0` → nothing matches this type with the current
 *   search term and zero filters — `nothingExists`, regardless of whether
 *   the search box is empty or not. The panel's own copy already only
 *   claims what's true either way ("nothing matches — with or without
 *   your quote filters"), and "Build it from products" is the right move
 *   whether the cause is a genuinely empty type or a search term that
 *   doesn't match anything of it — the fix is the same either way.
 */
export type ZeroResultState =
  | { kind: "hasResults" }
  | { kind: "loading" }
  | { kind: "genericEmpty" }
  | { kind: "filtersHideMatches"; relaxedCount: number }
  | { kind: "nothingExists" };

/**
 * Whether the placeholder's originally-seeded quote filters are the *only*
 * thing currently narrowing the search — nothing from the quick-filter row
 * (category, project type, a changed template type) and nothing from the
 * full filter modal's other fields (price, tags, created-by, created-date
 * range), and no property filter outside the seed. A seeded property whose
 * operator or value the user has since edited counts as *outside* the seed:
 * it is no longer something the quote carried over, so claiming it was would
 * be the same half-truth this whole check exists to avoid. This doesn't gate
 * `filtersHideMatches` — that fires on `relaxedCount` alone, regardless of
 * which filters are active — it only decides whether the panel's copy can
 * truthfully name "your quote's filters" specifically. Naming them when
 * something else is also active would be a half-truth: clearing only the
 * quote's filters wouldn't necessarily reveal anything, since "Clear
 * filters" clears everything the same way. Naming them when they're the
 * *entire* set is accurate and more informative than the generic fallback.
 */
export function isPureQuoteFiltersCause({
  activeSeededFilterCount,
  categories,
  typeOfProjects,
  typeOfTemplates,
  templateTypeIds,
  properties,
  priceMin,
  priceMax,
  createdAfter,
  createdBefore,
  createdByUserIds,
  tagsAny,
  tagsAll,
  seededPropertyFilters,
  seededTemplateTypeIds,
}: {
  activeSeededFilterCount: number;
  categories?: string[] | null;
  typeOfProjects?: string[] | null;
  typeOfTemplates?: string[] | null;
  templateTypeIds?: string[] | null;
  properties?: TemplateSearchFilterPropertyFilterInput[] | null;
  /** The rest of the full filter modal's fields (`TemplatesQuoteFilterModal.tsx`) —
   *  none of these have a seeded baseline, so any of them being set at all
   *  disqualifies the pure-quote-cause claim. */
  priceMin?: number | null;
  priceMax?: number | null;
  createdAfter?: unknown;
  createdBefore?: unknown;
  createdByUserIds?: string[] | null;
  tagsAny?: string[] | null;
  tagsAll?: string[] | null;
  /** The placeholder's original, fixed set — compared whole, not by key. */
  seededPropertyFilters: TemplateSearchFilterPropertyFilterInput[];
  seededTemplateTypeIds: string[];
}): boolean {
  if (activeSeededFilterCount === 0) return false;
  if ((categories?.length ?? 0) > 0) return false;
  if ((typeOfProjects?.length ?? 0) > 0) return false;
  if ((typeOfTemplates?.length ?? 0) > 0) return false;
  if (priceMin != null) return false;
  if (priceMax != null) return false;
  if (createdAfter != null) return false;
  if (createdBefore != null) return false;
  if ((createdByUserIds?.length ?? 0) > 0) return false;
  if ((tagsAny?.length ?? 0) > 0) return false;
  if ((tagsAll?.length ?? 0) > 0) return false;

  const currentTemplateTypeIds = templateTypeIds ?? [];
  const isSameTemplateTypeSelection =
    currentTemplateTypeIds.length === seededTemplateTypeIds.length &&
    currentTemplateTypeIds.every(id => seededTemplateTypeIds.includes(id));
  if (!isSameTemplateTypeSelection) return false;

  return (properties ?? []).every(active =>
    seededPropertyFilters.some(seeded => isEqual(seeded, active))
  );
}

/**
 * The seeded filters still active *and still carrying their seeded
 * definition* — the count the "carried over from this quote" copy names.
 * An edited one is excluded for the same reason it disqualifies the
 * quote-specific copy above: it is no longer what the quote carried over.
 */
export function countActiveSeededFilters({
  properties,
  seededPropertyFilters,
}: {
  properties?: TemplateSearchFilterPropertyFilterInput[] | null;
  seededPropertyFilters: TemplateSearchFilterPropertyFilterInput[];
}): number {
  return (properties ?? []).filter(active =>
    seededPropertyFilters.some(seeded => isEqual(seeded, active))
  ).length;
}

export function getZeroResultState({
  loading,
  filteredTotalCount,
  relaxedLoading,
  relaxedUnavailable,
  relaxedCount,
}: {
  /** The filtered `searchTemplates` query's own loading state. */
  loading: boolean;
  filteredTotalCount: number;
  /** Only meaningful once `filteredTotalCount === 0`. */
  relaxedLoading: boolean;
  /**
   * No count is coming: an Apollo error, or a settled protected result
   * (`MissingCapabilities`/`MissingPermissions`), which is *data* rather than
   * an error and so raises no error flag. Both leave the count permanently
   * absent, which is what separates them from `relaxedLoading`.
   */
  relaxedUnavailable: boolean;
  relaxedCount: number | undefined;
}): ZeroResultState {
  if (loading) return { kind: "loading" };
  if (filteredTotalCount > 0) return { kind: "hasResults" };
  // No count means neither branch below has a reliable premise — fall back
  // rather than guess one, and never sit on a spinner waiting for a count
  // that will never arrive.
  if (relaxedUnavailable) return { kind: "genericEmpty" };
  // Defaulting an unresolved count to 0 would flash "nothing exists yet"
  // and then flip to "filters hide matches" — treat it as still loading.
  if (relaxedLoading || relaxedCount === undefined) return { kind: "loading" };
  if (relaxedCount > 0) return { kind: "filtersHideMatches", relaxedCount };
  return { kind: "nothingExists" };
}

/**
 * Gates the relaxed count query behind Apollo's `skip`: only fired once the
 * filtered search has resolved to 0, and only in the placeholder-destination
 * flow that has a "quote" to carry filters over from in the first place.
 * Deliberately not gated on the search term or on which filters are
 * active — the relaxed query already accounts for both (see the module
 * doc above), so there's no case where skipping it here is more correct.
 */
export function shouldCheckRelaxedCount({
  hasDestination,
  loading,
  filteredTotalCount,
}: {
  hasDestination: boolean;
  loading: boolean;
  filteredTotalCount: number;
}): boolean {
  return hasDestination && !loading && filteredTotalCount === 0;
}

/**
 * The relaxed query's filters: keeps the template type constraint and the
 * other structural filters, but drops every quick/property filter — quote-
 * seeded or manually added — so the count only ever promises templates the
 * placeholder can hold, and only via the one lever ("Clear filters") the
 * panel's button actually pulls. The current search term is passed
 * alongside this (not part of this helper — see call site), since clearing
 * filters is not the same action as clearing search.
 */
/**
 * What the zero-result panel's "Clear filters" button must produce: every
 * quick/property filter dropped, the *current* template type kept.
 *
 * Keeping the type is what makes the button's count truthful. The relaxed
 * count is taken under the type in force at the time (D3 lets the user
 * re-scope it), so restoring the placeholder's seeded type instead would
 * reveal a different set than the one counted — and would silently throw the
 * re-scope away. Composed into the query the same way `filters` is, this
 * yields exactly `getRelaxedCountFilters`.
 */
export function clearFiltersKeepingTemplateType(
  filters: TemplateSearchFiltersInput
): TemplateSearchFiltersInput {
  return { templateTypeIds: filters.templateTypeIds };
}

export function getRelaxedCountFilters({
  fixedFilters,
  templateTypeIds,
  excludeTemplateIds,
  rootItemTypes,
  applicableFor,
}: {
  fixedFilters: TemplateSearchFiltersInput;
  templateTypeIds: TemplateSearchFiltersInput["templateTypeIds"];
  excludeTemplateIds?: string[];
  rootItemTypes?: ItemType[];
  applicableFor?: TplApplicableFor[];
}): TemplateSearchFiltersInput {
  return {
    ...fixedFilters,
    templateTypeIds,
    excludeTemplateIds,
    rootItemTypes,
    applicableFor,
  };
}
